import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import leadsApi from "../api/leadsApi";
import { useAuthStore } from "../store/useAuthStore";
import "./LeadDetail.css";

const PIPELINE_STAGES = [
  {
    key: "New",
    label: "New Lead",
    shortLabel: "New",
  },
  {
    key: "Intro Email Sent",
    label: "Intro Email",
    shortLabel: "Intro",
  },
  {
    key: "Portfolio Sent",
    label: "Portfolio Sent",
    shortLabel: "Portfolio",
  },
  {
    key: "Follow Up",
    label: "Follow Up",
    shortLabel: "Follow Up",
  },
  {
    key: "Meeting Done",
    label: "Meeting Done",
    shortLabel: "Meeting",
  },
  {
    key: "Proposal Sent",
    label: "Proposal Sent",
    shortLabel: "Proposal",
  },
  {
    key: "Negotiation",
    label: "Negotiation",
    shortLabel: "Negotiation",
  },
  {
    key: "Won",
    label: "Won",
    shortLabel: "Won",
  },
];
const LOST_REASONS = [
  "Price too high",
  "Selected another company",
  "Project postponed",
  "Project cancelled",
  "No response from client",
  "Budget not approved",
  "Scope mismatch",
  "Location not serviceable",
  "Timeline mismatch",
  "Not interested",
  "Duplicate lead",
  "Other",
];

const ACTION_TYPES = {
  INTRO_EMAIL: "Intro Email Sent",
  PORTFOLIO: "Portfolio Sent",
  MEETING: "Meeting Done",
  PROPOSAL: "Proposal Sent",
  NEGOTIATION: "Negotiation",
  WON: "Won",
  LOST: "Lost",
  NOTE: "Note Added",
  CALL: "Call Completed",
  FOLLOW_UP: "Follow-up Scheduled",
};

const MAIL_ACTION_CONFIG = {
  intro: {
    title: "Send Introduction Email",
    buttonLabel: "Send Intro Mail",
    status: ACTION_TYPES.INTRO_EMAIL,
  },
  portfolio: {
    title: "Send Portfolio",
    buttonLabel: "Send Portfolio",
    status: ACTION_TYPES.PORTFOLIO,
  },
  proposal: {
    title: "Send Proposal",
    buttonLabel: "Send Proposal",
    status: ACTION_TYPES.PROPOSAL,
  },
  followup: {
    title: "Send Follow-up Email",
    buttonLabel: "Send Follow-up Mail",
    status: ACTION_TYPES.FOLLOW_UP,
  },
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmailList = (values = []) => [
  ...new Set(
    values
      .flatMap((value) => String(value || "").split(/[,\s;]+/))
      .map((value) => value.trim().toLowerCase())
      .filter((value) => EMAIL_PATTERN.test(value)),
  ),
];

const formatDate = (value, includeTime = false) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(includeTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  }).format(date);
};

const toDateInputValue = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const toDateTimeInputValue = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatCurrency = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount) || amount <= 0) {
    return "—";
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
};

const getContactName = (lead) => {
  const fullName = `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim();

  return fullName || lead?.contact || "No contact name";
};

const getInitials = (value) => {
  if (!value) return "LD";

  return value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
};

const getPipelineIndex = (status, stages = PIPELINE_STAGES) => {
  const index = stages.findIndex((stage) => stage.key === status);

  return index < 0 ? 0 : index;
};

const getRequiredPreviousStage = (stageKey) => {
  const stageIndex = PIPELINE_STAGES.findIndex(
    (stage) => stage.key === stageKey,
  );

  if (stageIndex <= 0) return null;

  return PIPELINE_STAGES[stageIndex - 1];
};

const getLeadRecipientEmails = (lead) => {
  const emails = [];

  if (lead?.email) {
    emails.push(String(lead.email).trim().toLowerCase());
  }

  if (Array.isArray(lead?.teamMembers)) {
    lead.teamMembers.forEach((member) => {
      const memberEmail = String(member?.email || "")
        .trim()
        .toLowerCase();

      if (memberEmail) {
        emails.push(memberEmail);
      }
    });
  }

  return [...new Set(emails)].filter(Boolean);
};

const stripEmailHtml = (value = "") => {
  const raw = String(value || "");

  if (!raw) return "";

  if (typeof window !== "undefined" && typeof DOMParser !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(raw, "text/html");
      return String(doc.body?.textContent || "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
    } catch {
      // Fall through to the lightweight cleanup below.
    }
  }

  return raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#039;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const getEmailMessageTime = (message) =>
  message?.receivedAt || message?.sentAt || message?.createdAt || null;

const groupEmailMessages = (messages = []) => {
  const map = new Map();

  (Array.isArray(messages) ? messages : []).forEach((message) => {
    const key =
      message?.conversationId ||
      message?.inReplyTo ||
      message?.internetMessageId ||
      message?._id;

    if (!map.has(key)) {
      map.set(key, []);
    }

    map.get(key).push(message);
  });

  return [...map.entries()]
    .map(([key, items]) => {
      const sorted = [...items].sort(
        (first, second) =>
          new Date(getEmailMessageTime(first) || 0).getTime() -
          new Date(getEmailMessageTime(second) || 0).getTime(),
      );

      return {
        key,
        subject: sorted[0]?.subject || "Email conversation",
        messages: sorted,
        lastMessageAt: getEmailMessageTime(sorted[sorted.length - 1]),
      };
    })
    .sort(
      (first, second) =>
        new Date(second.lastMessageAt || 0).getTime() -
        new Date(first.lastMessageAt || 0).getTime(),
    );
};

const padScheduleNumber = (value) => String(value).padStart(2, "0");

const toLocalScheduleValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  return `${date.getFullYear()}-${padScheduleNumber(
    date.getMonth() + 1,
  )}-${padScheduleNumber(date.getDate())}T${padScheduleNumber(
    date.getHours(),
  )}:${padScheduleNumber(date.getMinutes())}`;
};

const getScheduleCalendarDays = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const start = new Date(firstDay);

  // Monday-first calendar.
  const mondayIndex = (firstDay.getDay() + 6) % 7;
  start.setDate(firstDay.getDate() - mondayIndex);

  return Array.from({ length: 42 }, (_item, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
};

const getNextScheduleDate = (daysToAdd, hour, minute = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + daysToAdd);
  date.setHours(hour, minute, 0, 0);
  return date;
};

const getNextWeekdayScheduleDate = (weekday, hour, minute = 0) => {
  const date = new Date();
  const currentDay = date.getDay();

  let daysToAdd = (weekday - currentDay + 7) % 7;
  if (daysToAdd === 0) daysToAdd = 7;

  date.setDate(date.getDate() + daysToAdd);
  date.setHours(hour, minute, 0, 0);

  return date;
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // App uses HashRouter. Using the hash directly here makes the
  // Congratulations modal's "View Won Leads" action reliable even
  // when this modal is rendered on top of the current lead detail page.
  const goToWonLeads = () => {
    setShowWonSuccess(false);
    setWonUndoSnapshot(null);

    if (typeof window !== "undefined") {
      window.location.hash = "#/won-leads";
      return;
    }

    navigate("/won-leads");
  };

  // Was reading a raw "user" key out of localStorage, which this app
  // never actually sets — auth state lives in useAuthStore (see App.jsx).
  // That meant currentRole was always "", so canManageProposal/isAdmin
  // were always false and the Proposal action never showed, even for
  // real admins.
  const { authUser } = useAuthStore();
  const currentUser = authUser || {};

  const currentRole = String(currentUser?.role || "")
    .trim()
    .toLowerCase();

  const canManageProposal = ["admin", "accountant"].includes(currentRole);
  const isAdmin = currentRole === "admin";

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [showWonSuccess, setShowWonSuccess] = useState(false);
  const [wonUndoSnapshot, setWonUndoSnapshot] = useState(null);
  const [undoingWon, setUndoingWon] = useState(false);

  const [activeTab, setActiveTab] = useState("activity");

  const [emailMessages, setEmailMessages] = useState([]);
  const [emailThreadLoading, setEmailThreadLoading] = useState(false);
  const [syncingReplies, setSyncingReplies] = useState(false);
  const [emailThreadError, setEmailThreadError] = useState("");
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [scheduledEmailsLoading, setScheduledEmailsLoading] = useState(false);
  const [editingScheduledEmail, setEditingScheduledEmail] = useState(null);
  const [deletingScheduledEmailId, setDeletingScheduledEmailId] = useState("");

  const [note, setNote] = useState("");

  const [showLostModal, setShowLostModal] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [customLostReason, setCustomLostReason] = useState("");

  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    meetingDate: "",
    meetingMode: "Online",
    meetingWith: "",
    meetingNotes: "",
  });

  const [showMailModal, setShowMailModal] = useState(false);
  const [sendingMail, setSendingMail] = useState(false);
  const [mailRecipientInput, setMailRecipientInput] = useState("");
  const [mailSendMode, setMailSendMode] = useState("now");
  const [scheduledDateTime, setScheduledDateTime] = useState("");
  const [scheduleCalendarMonth, setScheduleCalendarMonth] = useState(
    () => new Date(),
  );
  const [mailForm, setMailForm] = useState({
    actionType: "intro",
    to: [],
    cc: [],
    subject: "",
    message: "",
    attachments: [],
  });

  const [proposalForm, setProposalForm] = useState({
    proposalValue: "",
    proposalDate: new Date().toISOString().slice(0, 10),
    proposalReference: "",
    proposalNotes: "",
  });

  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpForm, setFollowUpForm] = useState({
    nextFollowUp: "",
    followUpNote: "",
  });

  const [savingTrackingDates, setSavingTrackingDates] = useState(false);
  const [trackingDates, setTrackingDates] = useState({
    lastContactDate: "",
    meetingDate: "",
    nextFollowUp: "",
  });

  /* =========================================================
     LOAD LEAD DETAIL
     ========================================================= */

  const fetchLead = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError("");

    try {
      let data;

      /*
        Preferred API:
        leadsApi.getById(id)

        Fallback:
        If getById is not available, load all leads and find this lead.
      */

      if (typeof leadsApi.getById === "function") {
        data = await leadsApi.getById(id);
      } else {
        const allLeads = await leadsApi.getAll();
        data = allLeads.find((item) => String(item._id) === String(id));
      }

      if (!data) {
        throw new Error("Lead not found.");
      }

      setLead(data);
    } catch (err) {
      setError(err?.message || "Unable to load lead details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  const fetchScheduledEmails = useCallback(async () => {
    if (!id || typeof leadsApi.getScheduledEmails !== "function") return;

    setScheduledEmailsLoading(true);
    try {
      const response = await leadsApi.getScheduledEmails(id);
      const items =
        response?.scheduledEmails || response?.data?.scheduledEmails || [];
      setScheduledEmails(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error("Unable to load scheduled emails", err);
    } finally {
      setScheduledEmailsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchScheduledEmails();
  }, [fetchScheduledEmails]);

  const getPendingScheduledEmail = useCallback(
    (actionType) =>
      scheduledEmails.find(
        (item) =>
          item?.actionType === actionType && item?.status === "scheduled",
      ) || null,
    [scheduledEmails],
  );

  const fetchEmailThread = useCallback(async () => {
    if (!id || typeof leadsApi.getEmailThread !== "function") {
      return;
    }

    setEmailThreadLoading(true);
    setEmailThreadError("");

    try {
      const response = await leadsApi.getEmailThread(id);
      const messages = response?.messages || response?.data?.messages || [];

      setEmailMessages(Array.isArray(messages) ? messages : []);
    } catch (err) {
      setEmailThreadError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load email thread.",
      );
    } finally {
      setEmailThreadLoading(false);
    }
  }, [id]);

  const syncClientReplies = useCallback(async () => {
    if (!id || typeof leadsApi.syncReplies !== "function") {
      setEmailThreadError(
        "leadsApi.syncReplies is missing. Replace leadsApi.js with the updated file.",
      );
      return;
    }

    setSyncingReplies(true);
    setEmailThreadError("");

    try {
      const response = await leadsApi.syncReplies(id);

      const messages = response?.messages || response?.data?.messages || [];

      setEmailMessages(Array.isArray(messages) ? messages : []);

      /*
        A reply updates lastContactDate/activity on backend,
        so refresh the lead after a successful sync.
      */
      if (Number(response?.synced || 0) > 0) {
        await fetchLead();
      }
    } catch (err) {
      setEmailThreadError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to sync client replies.",
      );
    } finally {
      setSyncingReplies(false);
    }
  }, [id, fetchLead]);

  /*
    When the Email Thread tab is opened, sync Outlook immediately.
    No page refresh is needed.
  */
  useEffect(() => {
    if (activeTab !== "email") return;

    syncClientReplies();
  }, [activeTab, syncClientReplies]);

  const emailThreads = useMemo(
    () => groupEmailMessages(emailMessages),
    [emailMessages],
  );

  useEffect(() => {
    setTrackingDates({
      lastContactDate: toDateInputValue(lead?.lastContactDate),
      meetingDate: toDateTimeInputValue(lead?.meetingDate),
      nextFollowUp: toDateInputValue(lead?.nextFollowUp),
    });
  }, [lead?.lastContactDate, lead?.meetingDate, lead?.nextFollowUp]);
  useEffect(() => {
    if (!trackingDates.lastContactDate) return;

    const followUp = new Date(trackingDates.lastContactDate);
    followUp.setDate(followUp.getDate() + 3);

    setTrackingDates((prev) => ({
      ...prev,
      nextFollowUp: followUp.toISOString().slice(0, 10),
    }));
  }, [trackingDates.lastContactDate]);
  const visiblePipelineStages = useMemo(
    () =>
      canManageProposal
        ? PIPELINE_STAGES
        : PIPELINE_STAGES.filter((stage) => stage.key !== "Proposal Sent"),
    [canManageProposal],
  );
  const activityHistory = useMemo(() => {
    const activities = Array.isArray(lead?.activities)
      ? [...lead.activities]
      : [];

    const visibleActivities = isAdmin
      ? activities
      : activities.filter((activity) => {
          const activityText = `${activity?.type || ""} ${
            activity?.title || ""
          }`.toLowerCase();

          return !activityText.includes("proposal");
        });

    return visibleActivities.sort((a, b) => {
      const firstDate = new Date(a.createdAt || a.date || 0).getTime();
      const secondDate = new Date(b.createdAt || b.date || 0).getTime();

      return secondDate - firstDate;
    });
  }, [lead, isAdmin]);

  const currentPipelineIndex = useMemo(
    () => getPipelineIndex(lead?.status, visiblePipelineStages),
    [lead?.status, visiblePipelineStages],
  );

  const introMailRecipients = useMemo(
    () => getLeadRecipientEmails(lead),
    [lead],
  );

  const stageCompletion = useMemo(
    () => ({
      New: true,
      "Intro Email Sent": Boolean(
        lead?.introEmailSentAt ||
        getPipelineIndex(lead?.status) >= getPipelineIndex("Intro Email Sent"),
      ),
      "Portfolio Sent": Boolean(
        lead?.portfolioSentAt ||
        getPipelineIndex(lead?.status) >= getPipelineIndex("Portfolio Sent"),
      ),
      "Meeting Done": Boolean(
        lead?.meetingDone ||
        lead?.meetingDate ||
        getPipelineIndex(lead?.status) >= getPipelineIndex("Meeting Done"),
      ),
      "Proposal Sent": Boolean(
        lead?.proposalSent ||
        lead?.proposalSentAt ||
        getPipelineIndex(lead?.status) >= getPipelineIndex("Proposal Sent"),
      ),
      Negotiation: Boolean(
        lead?.negotiationStartedAt ||
        ["Negotiation", "Won"].includes(lead?.status),
      ),
      Won: lead?.status === "Won",
    }),
    [lead],
  );

  const canCompleteStage = useCallback(
    (stageKey) => {
      const previousStage = getRequiredPreviousStage(stageKey);
      if (!previousStage) return true;
      return Boolean(stageCompletion[previousStage.key]);
    },
    [stageCompletion],
  );

  const showStageBlockedMessage = useCallback((stageKey) => {
    const previousStage = getRequiredPreviousStage(stageKey);
    if (!previousStage) return;

    const targetStage = PIPELINE_STAGES.find((stage) => stage.key === stageKey);
    window.alert(
      `Please complete "${previousStage.label}" before "${targetStage?.label || stageKey}".`,
    );
  }, []);
  const updateLead = async ({
    status,
    fields = {},
    activityType,
    activityTitle,
    activityDescription,
  }) => {
    if (!lead?._id || updating) return;

    setUpdating(true);
    setError("");

    try {
      const newActivity = {
        type: activityType || status || "Update",
        title: activityTitle || `Lead updated to ${status}`,
        description: activityDescription || "",
        createdAt: new Date().toISOString(),
      };

      const stageContactDate = new Date();

      const autoFollowUp = new Date(stageContactDate);
      autoFollowUp.setDate(autoFollowUp.getDate() + 3);

      const payload = {
        ...fields,
        ...(status ? { status } : {}),

        lastContactDate: stageContactDate.toISOString(),

        // Automatically set follow-up after 3 days
        nextFollowUp: fields.nextFollowUp || autoFollowUp.toISOString(),

        activities: [...(lead.activities || []), newActivity],
      };

      const response = await leadsApi.update(lead._id, payload);

      setLead((previous) => ({
        ...previous,
        ...payload,
        ...(response && typeof response === "object" ? response : {}),
      }));

      return true;
    } catch (err) {
      setError(err?.message || "Unable to update lead.");
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const saveTrackingDates = async () => {
    if (!lead?._id || savingTrackingDates) return;

    setSavingTrackingDates(true);
    setError("");

    try {
      const lastContact = trackingDates.lastContactDate
        ? new Date(`${trackingDates.lastContactDate}T12:00:00`)
        : null;

      const autoFollowUp = lastContact ? new Date(lastContact) : null;

      if (autoFollowUp) {
        autoFollowUp.setDate(autoFollowUp.getDate() + 3);
      }

      const fields = {
        lastContactDate: lastContact ? lastContact.toISOString() : null,

        meetingDate: trackingDates.meetingDate
          ? new Date(trackingDates.meetingDate).toISOString()
          : null,

        nextFollowUp: trackingDates.nextFollowUp
          ? new Date(`${trackingDates.nextFollowUp}T12:00:00`).toISOString()
          : autoFollowUp
            ? autoFollowUp.toISOString()
            : null,
      };

      const response = await leadsApi.update(lead._id, fields);

      setLead((previous) => ({
        ...previous,
        ...fields,
        ...(response && typeof response === "object" ? response : {}),
      }));

      window.alert("Lead dates updated successfully.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to update lead dates.",
      );
    } finally {
      setSavingTrackingDates(false);
    }
  };

  const selectedScheduleDate = useMemo(() => {
    if (!scheduledDateTime) return null;

    const parsed = new Date(scheduledDateTime);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [scheduledDateTime]);

  const scheduleCalendarDays = useMemo(
    () => getScheduleCalendarDays(scheduleCalendarMonth),
    [scheduleCalendarMonth],
  );

  const selectedScheduleTime = scheduledDateTime
    ? scheduledDateTime.slice(11, 16)
    : "";

  const chooseScheduleDate = (date) => {
    const chosen = new Date(date);

    let hour = 10;
    let minute = 0;

    if (selectedScheduleDate) {
      hour = selectedScheduleDate.getHours();
      minute = selectedScheduleDate.getMinutes();
    }

    chosen.setHours(hour, minute, 0, 0);

    // If the chosen time is already in the past, move it to one hour ahead.
    if (chosen.getTime() <= Date.now() + 30000) {
      const oneHourAhead = new Date(Date.now() + 60 * 60 * 1000);
      chosen.setHours(oneHourAhead.getHours(), oneHourAhead.getMinutes(), 0, 0);
    }

    setScheduledDateTime(toLocalScheduleValue(chosen));
  };

  const chooseScheduleTime = (timeValue) => {
    const [hours, minutes] = String(timeValue || "10:00")
      .split(":")
      .map(Number);

    const chosen = selectedScheduleDate
      ? new Date(selectedScheduleDate)
      : getNextScheduleDate(1, hours, minutes);

    chosen.setHours(hours, minutes, 0, 0);

    if (chosen.getTime() <= Date.now() + 30000) {
      window.alert("Please choose a future time.");
      return;
    }

    setScheduledDateTime(toLocalScheduleValue(chosen));
    setScheduleCalendarMonth(
      new Date(chosen.getFullYear(), chosen.getMonth(), 1),
    );
  };

  const chooseQuickSchedule = (date) => {
    setScheduledDateTime(toLocalScheduleValue(date));
    setScheduleCalendarMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  };

  /* =========================================================
     PIPELINE ACTIONS
     ========================================================= */
  // Portfolio and Proposal emails have different templates, so we build them separately.
  const buildMailTemplate = (actionType) => {
    const companyName = lead?.company || "Client";
    const contactName = getContactName(lead);

    if (actionType === "portfolio") {
      const plainMessage = `Dear ${contactName},

Greetings from SDC Studio.

I wanted to follow up on my previous email and share our SDC Studio portfolio for your reference.
Our portfolio highlights some of our recently completed BIM projects and provides an overview of our services, capabilities, and approach to supporting architecture, engineering, and construction teams.

Our experience includes:
• BIM Modeling & Revit Services
• Detailed Design & Construction Documentation
• BIM Coordination & Clash Detection
• Architectural Visualization
• Design Development & Project Delivery Support

We provide flexible BIM support throughout the project lifecycle, helping teams manage additional workload, meet project deadlines, and maintain consistent documentation quality.
Please find our portfolio attached for your reference.

Please let us know a convenient time to schedule a meeting or quick call. We would appreciate the opportunity to discuss your current or upcoming project requirements and explore how we can begin working together.

Regards,
SDC Studio
BIM • DIGITAL TWIN • IOT
business@sdcstudio.co
https://www.sdcstudio.co`;

      const messageHtml = `
    Dear ${contactName},<br><br>

    Greetings from SDC Studio.<br><br>

    I wanted to follow up on my previous email and share our
    <strong>SDC Studio portfolio</strong> for your reference.<br>

    Our portfolio highlights some of our recently completed BIM projects
    and provides an overview of our services, capabilities, and approach
    to supporting architecture, engineering, and construction teams.
    <br><br>

    Our experience includes:<br>
    • BIM Modeling & Revit Services<br>
    • Detailed Design & Construction Documentation<br>
    • BIM Coordination & Clash Detection<br>
    • Architectural Visualization<br>
    • Design Development & Project Delivery Support
    <br><br>

    We provide flexible BIM support throughout the project lifecycle,
    helping teams manage additional workload, meet project deadlines,
    and maintain consistent documentation quality.<br>

    Please find our portfolio attached for your reference.
    <br><br>

    Please let us know a
    <strong>convenient time to schedule a meeting or quick call.</strong>
    We would appreciate the opportunity to discuss your current or upcoming
    project requirements and explore how we can begin working together.
    <br><br>

    Regards,<br>
    SDC Studio<br>
    BIM • DIGITAL TWIN • IOT<br>
    business@sdcstudio.co<br>
    https://www.sdcstudio.co
  `;

      return {
        subject: "SDC Studio Portfolio",
        message: plainMessage,
        messageHtml,
      };
    }

    if (actionType === "followup") {
      return {
        subject: `Additional BIM Support for Upcoming Projects|SDC Studio,`,
        message: `Dear ${contactName},

Greetings from SDC Studio.

I wanted to share a practical resource from SDC Studio that may be useful for your team.

We recently published “BIM vs CAD: What’s the Difference?”, covering the practical differences between CAD and BIM workflows, including project documentation, coordination, model-based workflows, and how CAD information can be developed into BIM when required.

Read the article:
https://www.sdcstudio.co/post/bim-vs-cad

At SDC Studio, we support architecture and construction teams with:

• BIM & Revit Modeling
• BIM Coordination & Clash Detection
• Construction Documentation
• CAD-to-BIM Conversion
• Scan-to-BIM & As-Built Modeling
• BIM QA/QC
• Revit & Dynamo Automation

If your team has an upcoming project where additional BIM or Revit support could be useful, I'd be happy to learn more about your requirements.

Would you be available for a 15-minute call sometime next week to briefly introduce our capabilities and understand your current workflow?

If a call isn't convenient, I'm also happy to continue the conversation by email.

Regards,
SDC Studio
BIM • DIGITAL TWIN • IOT
business@sdcstudio.co
www.sdcstudio.co`,
      };
    }

    if (actionType === "proposal") {
      return {
        subject: `Proposal from SDC Studio - ${companyName}`,
        message: `Dear ${contactName},

Greetings from SDC Studio.

Please find our proposal for your review.



${proposalForm.proposalNotes || ""}

Please review the proposal and let us know if any clarification is required.

Regards,
SDC Studio
https://www.sdcllp.com`,
      };
    }

    return {
      subject: `SDC Studio | BIM & Architectural Design Support`,

      message: `Dear ${contactName},

Greetings from SDC Studio.

Thank you for giving us the opportunity to connect with ${companyName}.

I’m reaching out to introduce SDC Studio, a BIM and architectural design support firm providing reliable BIM solutions for architecture, engineering, and construction teams.

Our key capabilities include::
•	BIM & Revit Modeling
•	Detailed Design & BIM Development
•	Construction, Permit & Bid Documentation
•	BIM Coordination & Clash Detection
•	BIM QA/QC & Model Auditing
•	Scan-to-BIM & As-Built Modeling
•	Revit Families & Content Development
•	Quantity Take-Offs & Cost Estimation
•	Revit & Dynamo Automation
•	Digital Twin & Smart Building Solutions

Our team works as an extension of your in-house BIM team, providing flexible support when you have peak workloads, tight deadlines, project backlogs, or additional BIM requirements.

We work within your existing standards, templates, workflows, and project requirements, with a focus on accurate modeling, coordinated documentation, quality control, and efficient project delivery.

I’ve attached our SDC Studio Portfolio for your review. It provides an overview of our capabilities and selected project experience.

If you have any current or upcoming projects where additional BIM support could be useful, we would be glad to learn more about your requirements.

Would you be open to a brief 15–20 minute introduction call?

Thank you for your time.

Regards,
SDC Studio
 BIM • DIGITAL TWIN • IOT
business@sdcstudio.co
www.sdcstudio.co`,
    };
  };

  const openMailBox = (actionType) => {
    const actionStageMap = {
      intro: ACTION_TYPES.INTRO_EMAIL,
      portfolio: ACTION_TYPES.PORTFOLIO,
      proposal: ACTION_TYPES.PROPOSAL,
    };

    const targetStage = actionStageMap[actionType];
    const existingSchedule = getPendingScheduledEmail(actionType);

    if (!existingSchedule && targetStage && !canCompleteStage(targetStage)) {
      showStageBlockedMessage(targetStage);
      return;
    }

    if (actionType === "proposal" && !canManageProposal) {
      window.alert("Only Admin can send proposals.");
      return;
    }

    if (existingSchedule) {
      setEditingScheduledEmail(existingSchedule);
      setMailRecipientInput("");
      setMailSendMode("schedule");
      setScheduledDateTime(
        toLocalScheduleValue(new Date(existingSchedule.scheduledFor)),
      );
      setScheduleCalendarMonth(new Date(existingSchedule.scheduledFor));
      setMailForm({
        actionType,
        to: normalizeEmailList(existingSchedule.to || []),
        cc: normalizeEmailList(existingSchedule.cc || []),
        subject: existingSchedule.subject || "",
        message: existingSchedule.message || "",
        attachments: [],
      });
      if (actionType === "proposal") {
        setProposalForm({
          proposalValue: existingSchedule.proposalValue ?? "",
          proposalDate: existingSchedule.proposalDate
            ? String(existingSchedule.proposalDate).slice(0, 10)
            : new Date().toISOString().slice(0, 10),
          proposalReference: existingSchedule.proposalReference || "",
          proposalNotes: existingSchedule.proposalNotes || "",
        });
      }
      setShowMailModal(true);
      return;
    }

    const recipients = getLeadRecipientEmails(lead);
    if (!recipients.length) {
      window.alert(
        "Primary contact and added team members do not have a valid email address.",
      );
      return;
    }

    const template = buildMailTemplate(actionType);
    setEditingScheduledEmail(null);
    setMailRecipientInput("");
    setMailSendMode("now");
    setScheduledDateTime("");
    setScheduleCalendarMonth(new Date());
    setMailForm({
      actionType,
      to: recipients,
      cc: [],
      subject: template.subject,
      message: template.message,
      attachments: [],
    });
    setShowMailModal(true);
  };

  const addMailRecipients = () => {
    const newRecipients = normalizeEmailList([mailRecipientInput]);

    if (!newRecipients.length) {
      window.alert("Please enter a valid email address.");
      return;
    }

    setMailForm((previous) => ({
      ...previous,
      to: normalizeEmailList([...previous.to, ...newRecipients]),
    }));
    setMailRecipientInput("");
  };

  const removeMailRecipient = (email) => {
    setMailForm((previous) => ({
      ...previous,
      to: previous.to.filter((item) => item !== email),
    }));
  };

  const handleMailFiles = (event) => {
    const files = Array.from(event.target.files || []);
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024;

    const acceptedFiles = files.filter((file) => {
      if (file.size > maxSize) {
        window.alert(`${file.name} is larger than 10 MB.`);
        return false;
      }
      return true;
    });

    setMailForm((previous) => ({
      ...previous,
      attachments: [...previous.attachments, ...acceptedFiles].slice(
        0,
        maxFiles,
      ),
    }));

    event.target.value = "";
  };

  const removeMailAttachment = (index) => {
    setMailForm((previous) => ({
      ...previous,
      attachments: previous.attachments.filter(
        (_file, fileIndex) => fileIndex !== index,
      ),
    }));
  };

  const sendMailFromLeadPage = async (event) => {
    event.preventDefault();

    if (!lead?._id || sendingMail) return;

    if (mailForm.actionType === "proposal" && !canManageProposal) {
      window.alert("Only Admin can send proposals.");
      return;
    }

    const recipients = normalizeEmailList(mailForm.to);

    if (!recipients.length) {
      window.alert("Please add at least one valid recipient.");
      return;
    }

    if (!mailForm.subject.trim()) {
      window.alert("Please enter the email subject.");
      return;
    }

    if (!mailForm.message.trim()) {
      window.alert("Please enter the email message.");
      return;
    }

    if (mailSendMode === "schedule") {
      if (!scheduledDateTime) {
        window.alert("Please select schedule date and time.");
        return;
      }

      const scheduleDate = new Date(scheduledDateTime);
      if (
        Number.isNaN(scheduleDate.getTime()) ||
        scheduleDate.getTime() <= Date.now() + 30000
      ) {
        window.alert("Please select a future schedule date and time.");
        return;
      }
    }

    setSendingMail(true);
    setError("");

    try {
      const formData = new FormData();

      formData.append("actionType", mailForm.actionType);
      formData.append("to", JSON.stringify(recipients));
      formData.append("cc", JSON.stringify(normalizeEmailList(mailForm.cc)));
      formData.append("subject", mailForm.subject.trim());
      formData.append("message", mailForm.message.trim());
      formData.append("scheduleMode", mailSendMode);

      if (mailSendMode === "schedule") {
        formData.append(
          "scheduledFor",
          new Date(scheduledDateTime).toISOString(),
        );
        formData.append(
          "timezone",
          Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata",
        );
      }

      if (mailForm.actionType === "proposal") {
        formData.append(
          "proposalValue",
          String(Number(proposalForm.proposalValue || 0)),
        );
        formData.append("proposalDate", proposalForm.proposalDate || "");
        formData.append(
          "proposalReference",
          proposalForm.proposalReference || "",
        );
        formData.append("proposalNotes", proposalForm.proposalNotes || "");
      }

      mailForm.attachments.forEach((file) => {
        formData.append("attachments", file);
      });

      if (typeof leadsApi.sendEmail !== "function") {
        throw new Error(
          "leadsApi.sendEmail is missing. Add the supplied sendEmail method to leadsApi.js.",
        );
      }

      const response =
        editingScheduledEmail && mailSendMode === "schedule"
          ? await leadsApi.updateScheduledEmail(
              lead._id,
              editingScheduledEmail._id,
              formData,
            )
          : await leadsApi.sendEmail(lead._id, formData);
      const updatedLead =
        response?.lead || response?.data?.lead || response?.data || response;

      if (updatedLead && typeof updatedLead === "object") {
        setLead(updatedLead);
      } else {
        await fetchLead();
      }

      if (response?.scheduled) {
        await fetchScheduledEmails();
      } else if (typeof leadsApi.getEmailThread === "function") {
        await fetchEmailThread();
        await fetchScheduledEmails();
      }

      setShowMailModal(false);
      setEditingScheduledEmail(null);
      setMailSendMode("now");
      setScheduledDateTime("");
      setMailForm({
        actionType: "intro",
        to: [],
        cc: [],
        subject: "",
        message: "",
        attachments: [],
      });

      if (mailForm.actionType === "proposal") {
        setProposalForm({
          proposalValue: "",
          proposalDate: new Date().toISOString().slice(0, 10),
          proposalReference: "",
          proposalNotes: "",
        });
      }

      if (response?.scheduled) {
        window.alert(
          `${editingScheduledEmail ? "Schedule updated" : "Email scheduled"} successfully for ${formatDate(
            response.scheduledFor,
            true,
          )}. It will show as Sent only after actual delivery.`,
        );
      } else {
        window.alert(
          `Email sent successfully to ${recipients.length} recipient${
            recipients.length === 1 ? "" : "s"
          }. Sales pipeline updated.`,
        );
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Unable to send email.",
      );
    } finally {
      setSendingMail(false);
    }
  };

  const deleteScheduledEmail = async (scheduledEmail) => {
    if (!lead?._id || !scheduledEmail?._id || deletingScheduledEmailId) return;

    const ok = window.confirm(
      `Delete this scheduled email for ${formatDate(scheduledEmail.scheduledFor, true)}?`,
    );
    if (!ok) return;

    setDeletingScheduledEmailId(String(scheduledEmail._id));
    try {
      await leadsApi.deleteScheduledEmail(lead._id, scheduledEmail._id);
      await Promise.all([fetchScheduledEmails(), fetchLead()]);
      if (editingScheduledEmail?._id === scheduledEmail._id) {
        setShowMailModal(false);
        setEditingScheduledEmail(null);
      }
      window.alert("Scheduled email deleted successfully.");
    } catch (err) {
      window.alert(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to delete scheduled email.",
      );
    } finally {
      setDeletingScheduledEmailId("");
    }
  };

  const sendIntroEmail = () => openMailBox("intro");
  const sendPortfolio = () => openMailBox("portfolio");
  const sendProposal = () => openMailBox("proposal");
  const sendFollowUpEmail = () => openMailBox("followup");
  const saveMeeting = async (event) => {
    event.preventDefault();

    if (!canCompleteStage(ACTION_TYPES.MEETING)) {
      showStageBlockedMessage(ACTION_TYPES.MEETING);
      return;
    }

    if (!meetingForm.meetingDate) {
      window.alert("Please select the meeting date.");
      return;
    }

    const success = await updateLead({
      status: ACTION_TYPES.MEETING,
      fields: {
        meetingDone: true,
        meetingDate: meetingForm.meetingDate,
        meetingMode: meetingForm.meetingMode,
        meetingWith: meetingForm.meetingWith,
        meetingNotes: meetingForm.meetingNotes,
      },
      activityType: "meeting",
      activityTitle: "Client meeting completed",
      activityDescription: [
        meetingForm.meetingMode,
        meetingForm.meetingWith
          ? `Meeting with ${meetingForm.meetingWith}`
          : "",
        meetingForm.meetingNotes,
      ]
        .filter(Boolean)
        .join(" • "),
    });

    if (success) {
      setShowMeetingModal(false);
      setMeetingForm({
        meetingDate: "",
        meetingMode: "Online",
        meetingWith: "",
        meetingNotes: "",
      });
    }
  };

  const startNegotiation = async () => {
    if (!canCompleteStage(ACTION_TYPES.NEGOTIATION)) {
      showStageBlockedMessage(ACTION_TYPES.NEGOTIATION);
      return;
    }

    await updateLead({
      status: ACTION_TYPES.NEGOTIATION,
      fields: {
        negotiationStartedAt: new Date().toISOString(),
      },
      activityType: "negotiation",
      activityTitle: "Negotiation started",
      activityDescription:
        "Commercial or project-scope negotiation started with the client.",
    });
  };

  const markAsWon = async () => {
    if (!lead?._id || updating || lead?.status === ACTION_TYPES.WON) {
      return;
    }

    const previousStage = lead?.status || "New";

    // Keep the exact pre-Won values so the X button can safely undo
    // an accidental click and return the lead to the stage it came from.
    const snapshot = {
      status: previousStage,
      wonAt: lead?.wonAt || null,
      probability: lead?.probability === undefined ? null : lead.probability,
      lostReason: lead?.lostReason || "",
      lostAt: lead?.lostAt || null,
      lastContactDate: lead?.lastContactDate || null,
      nextFollowUp: lead?.nextFollowUp || null,
      activities: Array.isArray(lead?.activities) ? [...lead.activities] : [],
    };

    setWonUndoSnapshot(snapshot);

    const success = await updateLead({
      status: ACTION_TYPES.WON,
      fields: {
        wonAt: new Date().toISOString(),
        lostReason: "",
        lostAt: null,
        probability: 100,
      },
      activityType: "won",
      activityTitle: "Lead closed as Won",
      activityDescription: `Client confirmed the project. Lead was closed as Won directly from "${previousStage}".`,
    });

    if (success) {
      setShowWonSuccess(true);
    } else {
      setWonUndoSnapshot(null);
    }
  };

  const undoWon = async () => {
    if (!lead?._id || !wonUndoSnapshot || undoingWon) {
      setShowWonSuccess(false);
      return;
    }

    setUndoingWon(true);
    setError("");

    try {
      const restorePayload = {
        status: wonUndoSnapshot.status,
        wonAt: wonUndoSnapshot.wonAt,
        probability: wonUndoSnapshot.probability,
        lostReason: wonUndoSnapshot.lostReason,
        lostAt: wonUndoSnapshot.lostAt,
        lastContactDate: wonUndoSnapshot.lastContactDate,
        nextFollowUp: wonUndoSnapshot.nextFollowUp,
        activities: wonUndoSnapshot.activities,
      };

      const response = await leadsApi.update(lead._id, restorePayload);
      const restoredLead =
        response?.lead || response?.data?.lead || response?.data || response;

      setLead((previous) => ({
        ...previous,
        ...restorePayload,
        ...(restoredLead && typeof restoredLead === "object"
          ? restoredLead
          : {}),
      }));

      setShowWonSuccess(false);
      setWonUndoSnapshot(null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to undo Won status.",
      );
    } finally {
      setUndoingWon(false);
    }
  };

  const markAsLost = async (event) => {
    event.preventDefault();

    const finalReason =
      lostReason === "Other" ? customLostReason.trim() : lostReason;

    if (!finalReason) {
      window.alert("Please select or enter the lost reason.");
      return;
    }

    const success = await updateLead({
      status: ACTION_TYPES.LOST,
      fields: {
        lostReason: finalReason,
        lostAt: new Date().toISOString(),
        probability: 0,
      },
      activityType: "lost",
      activityTitle: "Lead marked as Lost",
      activityDescription: `Lost reason: ${finalReason}`,
    });

    if (success) {
      setShowLostModal(false);
      setLostReason("");
      setCustomLostReason("");
    }
  };

  const addNote = async (event) => {
    event.preventDefault();

    const cleanNote = note.trim();

    if (!cleanNote || !lead?._id || updating) {
      return;
    }

    setUpdating(true);
    setError("");

    try {
      const response = await leadsApi.addActivity(lead._id, {
        type: "note",
        title: "Note added",
        description: cleanNote,
      });

      const updatedLead = response?.data || response;

      if (updatedLead) {
        setLead(updatedLead);
      } else {
        await fetchLead();
      }

      setNote("");
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Unable to save note.",
      );
    } finally {
      setUpdating(false);
    }
  };

  const saveFollowUp = async (event) => {
    event.preventDefault();

    if (!followUpForm.nextFollowUp) {
      window.alert("Please select the follow-up date.");
      return;
    }

    const success = await updateLead({
      fields: {
        nextFollowUp: followUpForm.nextFollowUp,
      },
      activityType: "follow-up",
      activityTitle: "Follow-up scheduled",
      activityDescription: [
        `Follow-up date: ${formatDate(followUpForm.nextFollowUp)}`,
        followUpForm.followUpNote,
      ]
        .filter(Boolean)
        .join(" • "),
    });

    if (success) {
      setShowFollowUpModal(false);
      setFollowUpForm({
        nextFollowUp: "",
        followUpNote: "",
      });
    }
  };

  const markCallCompleted = async () => {
    await updateLead({
      fields: {},
      activityType: "call",
      activityTitle: "Phone call completed",
      activityDescription: `Phone call completed with ${getContactName(lead)}.`,
    });
  };

  /* =========================================================
     STATES
     ========================================================= */

  if (loading) {
    return (
      <div className="lead-detail-state-page">
        <div className="lead-detail-loader" />
        <div>Loading lead details...</div>
      </div>
    );
  }

  if (error && !lead) {
    return (
      <div className="lead-detail-state-page">
        <div className="lead-detail-error-card">
          <h3 className="lead-detail-error-title">Unable to open lead</h3>

          <p className="lead-detail-error-text">{error}</p>

          <button
            type="button"
            className="lead-detail-primary-button"
            onClick={() => navigate("/leads")}
          >
            Back to Leads
          </button>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  const isWon = lead.status === "Won";
  const isLost = lead.status === "Lost";
  const isClosed = isWon || isLost;

  return (
    <div className="lead-detail-page">
      {showWonSuccess && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            background: "rgba(15, 23, 42, 0.58)",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 460,
              overflow: "hidden",
              borderRadius: 22,
              background: "#ffffff",
              boxShadow: "0 28px 80px rgba(15, 23, 42, 0.28)",
              animation: "leadWonPop 0.25s ease",
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={undoWon}
              disabled={undoingWon}
              title="Undo Won and return this lead to its previous stage"
              aria-label="Undo Won"
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                zIndex: 2,
                width: 34,
                height: 34,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #fecaca",
                borderRadius: "50%",
                background: "#ffffff",
                color: "#dc2626",
                fontSize: 21,
                fontWeight: 800,
                lineHeight: 1,
                cursor: undoingWon ? "wait" : "pointer",
                boxShadow: "0 5px 14px rgba(15, 23, 42, 0.08)",
                opacity: undoingWon ? 0.65 : 1,
              }}
            >
              {undoingWon ? "…" : "×"}
            </button>

            <div
              style={{
                padding: "34px 28px 26px",
                textAlign: "center",
                background:
                  "linear-gradient(145deg, #ecfdf5 0%, #ffffff 55%, #f0fdf4 100%)",
              }}
            >
              <div
                style={{
                  width: 82,
                  height: 82,
                  margin: "0 auto 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  background: "#16a34a",
                  color: "#ffffff",
                  fontSize: 40,
                  fontWeight: 900,
                  boxShadow: "0 12px 30px rgba(22, 163, 74, 0.28)",
                }}
              >
                ✓
              </div>

              <div
                style={{
                  marginBottom: 7,
                  color: "#16a34a",
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                }}
              >
                Deal Closed Successfully
              </div>

              <h2
                style={{
                  margin: 0,
                  color: "#0f172a",
                  fontSize: 27,
                  lineHeight: 1.2,
                  fontWeight: 900,
                }}
              >
                Congratulations! 🎉
              </h2>

              <p
                style={{
                  margin: "12px auto 0",
                  maxWidth: 350,
                  color: "#64748b",
                  fontSize: 14,
                  lineHeight: 1.65,
                }}
              >
                <strong style={{ color: "#166534" }}>
                  {lead?.company || getContactName(lead)}
                </strong>{" "}
                has been marked as a Won opportunity.
              </p>

              <div
                style={{
                  marginTop: 20,
                  padding: "13px 15px",
                  border: "1px solid #bbf7d0",
                  borderRadius: 12,
                  background: "#f0fdf4",
                  color: "#166534",
                  fontSize: 12.5,
                  fontWeight: 700,
                }}
              >
                The lead has moved to Won Leads and the sales pipeline is now
                closed successfully.
              </div>

              <div
                style={{
                  marginTop: 9,
                  color: "#94a3b8",
                  fontSize: 10.5,
                  lineHeight: 1.5,
                }}
              >
                Marked Won by mistake? Click the red × in the top-right corner
                to restore this lead to its previous stage.
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                padding: "18px 22px 22px",
                background: "#ffffff",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setShowWonSuccess(false);
                  setWonUndoSnapshot(null);
                }}
                style={{
                  flex: 1,
                  height: 44,
                  border: "1px solid #d7dee9",
                  borderRadius: 10,
                  background: "#ffffff",
                  color: "#334155",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                See Lead
              </button>

              <button
                type="button"
                onClick={goToWonLeads}
                style={{
                  flex: 1.3,
                  height: 44,
                  border: "none",
                  borderRadius: 10,
                  background: "#16a34a",
                  color: "#ffffff",
                  fontSize: 13,
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: "0 8px 18px rgba(22, 163, 74, 0.2)",
                }}
              >
                View Won Leads →
              </button>
            </div>
          </div>

          <style>
            {`
              @keyframes leadWonPop {
                from {
                  opacity: 0;
                  transform: translateY(16px) scale(0.96);
                }
                to {
                  opacity: 1;
                  transform: translateY(0) scale(1);
                }
              }
            `}
          </style>
        </div>
      )}

      {/* =====================================================
          TOP HEADER
          ===================================================== */}

      <header className="lead-detail-header">
        <div className="lead-detail-header-left">
          <button
            type="button"
            className="lead-detail-back-button"
            onClick={() => navigate("/leads")}
          >
            ←
          </button>

          <div className="lead-detail-company-avatar">
            {getInitials(lead.company || getContactName(lead))}
          </div>

          <div>
            <div className="lead-detail-record-label">LEAD</div>

            <h1 className="lead-detail-company-name">
              {lead.company || "Unnamed Company"}
            </h1>

            <div className="lead-detail-header-subtitle">
              {getContactName(lead)}
              {lead.designation ? ` • ${lead.designation}` : ""}
            </div>
          </div>
        </div>

        <div className="lead-detail-header-actions">
          <button
            type="button"
            className="lead-detail-secondary-button"
            onClick={() =>
              navigate("/leads", {
                state: {
                  editLeadId: lead._id,
                },
              })
            }
          >
            Edit Lead
          </button>

          {!isClosed && (
            <>
              <button
                type="button"
                className="lead-detail-won-button"
                onClick={markAsWon}
                disabled={updating}
              >
                {updating ? "Saving..." : "✓ Mark Won"}
              </button>

              <button
                type="button"
                className="lead-detail-lost-button"
                onClick={() => setShowLostModal(true)}
                disabled={updating}
              >
                × Mark Lost
              </button>
            </>
          )}
        </div>
      </header>

      {error && <div className="lead-detail-error-banner">{error}</div>}

      {/* =====================================================
          STATUS SUMMARY
          ===================================================== */}

      <section className="lead-detail-summary-grid">
        <SummaryCard
          title="Lead Status"
          value={lead.status || "New"}
          valueClassName={
            isWon
              ? "lead-detail-summary-value-won"
              : isLost
                ? "lead-detail-summary-value-lost"
                : "lead-detail-summary-value-active"
          }
        />

        <SummaryCard
          title="Estimated Value"
          value={formatCurrency(lead.estValue || lead.proposalValue)}
        />

        <SummaryCard title="Priority" value={lead.priority || "Medium"} />

        <SummaryCard
          title="Next Follow-up"
          value={formatDate(lead.nextFollowUp)}
        />

        <SummaryCard
          title="Assigned To"
          value={lead.assignedTo || "Not assigned"}
        />
      </section>

      {/* =====================================================
          SALES PIPELINE
          ===================================================== */}

      <section className="lead-detail-pipeline-card">
        <div className="lead-detail-section-heading-row">
          <div>
            <h2 className="lead-detail-section-title">Sales Pipeline</h2>
            <p className="lead-detail-section-description">
              Track this lead from initial contact to project conversion.
            </p>
          </div>

          {updating && (
            <span className="lead-detail-saving-text">Saving changes...</span>
          )}
        </div>

        {isLost ? (
          <div className="lead-detail-lost-status-box">
            <div className="lead-detail-lost-icon">×</div>

            <div>
              <strong className="lead-detail-lost-status-title">
                Lead Lost
              </strong>

              <div className="lead-detail-lost-status-reason">
                Reason: {lead.lostReason || "Reason not provided"}
              </div>

              {lead.lostAt && (
                <div className="lead-detail-lost-status-date">
                  Closed on {formatDate(lead.lostAt)}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="lead-detail-pipeline">
            {visiblePipelineStages.map((stage, index) => {
              const completed = index < currentPipelineIndex;
              const current =
                index === currentPipelineIndex || stage.key === lead.status;

              const wonStage = stage.key === "Won" && isWon;

              return (
                <React.Fragment key={stage.key}>
                  <button
                    type="button"
                    className={[
                      "lead-detail-pipeline-stage",
                      completed ? "lead-detail-pipeline-stage-completed" : "",
                      current ? "lead-detail-pipeline-stage-current" : "",
                      wonStage ? "lead-detail-pipeline-stage-won" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      if (stage.key === ACTION_TYPES.WON) {
                        markAsWon();
                        return;
                      }

                      if (stageCompletion[stage.key]) return;

                      if (!canCompleteStage(stage.key)) {
                        showStageBlockedMessage(stage.key);
                        return;
                      }

                      window.alert(
                        `Complete "${stage.label}" from Quick Actions below.`,
                      );
                    }}
                    disabled={updating || isClosed}
                  >
                    <span
                      className={[
                        "lead-detail-pipeline-circle",
                        completed
                          ? "lead-detail-pipeline-circle-completed"
                          : "",
                        current ? "lead-detail-pipeline-circle-current" : "",
                        wonStage ? "lead-detail-pipeline-circle-won" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {completed || wonStage ? "✓" : index + 1}
                    </span>

                    <span className="lead-detail-pipeline-label">
                      {stage.shortLabel}
                    </span>
                  </button>

                  {index < visiblePipelineStages.length - 1 && (
                    <div
                      className={[
                        "lead-detail-pipeline-line",
                        index < currentPipelineIndex
                          ? "lead-detail-pipeline-line-completed"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        <div className="lead-detail-tracking-panel">
          <div className="lead-detail-tracking-header">
            <div>
              <h3 className="lead-detail-tracking-title">
                Contact & Schedule Dates
              </h3>
              <p className="lead-detail-tracking-description">
                These values are fetched from this lead and can be edited here.
                Last Contacted On updates automatically whenever a pipeline
                stage is completed.
              </p>
            </div>

            <button
              type="button"
              className="lead-detail-primary-button"
              onClick={saveTrackingDates}
              disabled={savingTrackingDates || updating || isClosed}
            >
              {savingTrackingDates ? "Saving..." : "Save Dates"}
            </button>
          </div>

          <div className="lead-detail-tracking-grid">
            <label className="lead-detail-tracking-field">
              <span>Last Contacted On</span>
              <input
                type="date"
                className="lead-detail-input"
                value={trackingDates.lastContactDate}
                onChange={(event) =>
                  setTrackingDates((previous) => ({
                    ...previous,
                    lastContactDate: event.target.value,
                  }))
                }
                disabled={savingTrackingDates || isClosed}
              />
              <small>
                Automatically changes after Intro Email, Portfolio, Meeting,
                Proposal, Negotiation, Call, Follow-up or Won action.
              </small>
            </label>

            <label className="lead-detail-tracking-field">
              <span>Meeting Schedule</span>
              <input
                type="datetime-local"
                className="lead-detail-input"
                value={trackingDates.meetingDate}
                onChange={(event) =>
                  setTrackingDates((previous) => ({
                    ...previous,
                    meetingDate: event.target.value,
                  }))
                }
                disabled={savingTrackingDates || isClosed}
              />
              <small>
                {lead.meetingMode
                  ? `Meeting mode: ${lead.meetingMode}`
                  : "Select the meeting date and time."}
              </small>
            </label>

            <label className="lead-detail-tracking-field">
              <span>Follow-up On</span>
              <input
                type="date"
                className="lead-detail-input"
                value={trackingDates.nextFollowUp}
                onChange={(event) =>
                  setTrackingDates((previous) => ({
                    ...previous,
                    nextFollowUp: event.target.value,
                  }))
                }
                disabled={savingTrackingDates || isClosed}
              />
              <small>
                This is fetched from the lead's Next Follow-up field.
              </small>
            </label>
          </div>
        </div>
      </section>

      {/* =====================================================
          MAIN CONTENT
          ===================================================== */}

      <div className="lead-detail-main-grid">
        <main>
          {/* Quick actions */}

          <section className="lead-detail-card">
            <h2 className="lead-detail-section-title">Quick Actions</h2>

            <div className="lead-detail-quick-actions-grid">
              <ActionButton
                icon={getPendingScheduledEmail("intro") ? "◷" : "✉"}
                title={
                  lead.introEmailSentAt
                    ? "Intro Mail Sent"
                    : getPendingScheduledEmail("intro")
                      ? "Intro Mail Scheduled"
                      : "Send Intro Mail"
                }
                description={
                  getPendingScheduledEmail("intro")
                    ? `Scheduled: ${formatDate(getPendingScheduledEmail("intro").scheduledFor, true)} • Click to edit`
                    : "Send or schedule introduction email."
                }
                completed={Boolean(lead.introEmailSentAt)}
                onClick={sendIntroEmail}
                disabled={updating || isClosed}
              />

              <ActionButton
                icon={getPendingScheduledEmail("portfolio") ? "◷" : "▣"}
                title={
                  lead.portfolioSentAt
                    ? "Portfolio Sent"
                    : getPendingScheduledEmail("portfolio")
                      ? "Portfolio Scheduled"
                      : "Send Portfolio"
                }
                description={
                  getPendingScheduledEmail("portfolio")
                    ? `Scheduled: ${formatDate(getPendingScheduledEmail("portfolio").scheduledFor, true)} • Click to edit`
                    : "Send or schedule the company portfolio."
                }
                completed={Boolean(lead.portfolioSentAt)}
                onClick={sendPortfolio}
                disabled={
                  updating ||
                  isClosed ||
                  !canCompleteStage(ACTION_TYPES.PORTFOLIO)
                }
              />
              <ActionButton
                icon="✉"
                title="Send Follow-up Mail"
                description="Send or schedule a follow-up email."
                completed={false}
                onClick={sendFollowUpEmail}
                disabled={updating || isClosed}
              />
              <ActionButton
                icon="◉"
                title="Meeting Done"
                description="Record client meeting details."
                completed={Boolean(lead.meetingDone)}
                onClick={() => {
                  if (!canCompleteStage(ACTION_TYPES.MEETING)) {
                    showStageBlockedMessage(ACTION_TYPES.MEETING);
                    return;
                  }
                  setShowMeetingModal(true);
                }}
                disabled={
                  updating ||
                  isClosed ||
                  !canCompleteStage(ACTION_TYPES.MEETING)
                }
              />

              {canManageProposal && (
                <ActionButton
                  icon={getPendingScheduledEmail("proposal") ? "◷" : "₹"}
                  title={
                    lead.proposalSent
                      ? "Proposal Sent"
                      : getPendingScheduledEmail("proposal")
                        ? "Proposal Scheduled"
                        : "Send Proposal"
                  }
                  description={
                    getPendingScheduledEmail("proposal")
                      ? `Scheduled: ${formatDate(getPendingScheduledEmail("proposal").scheduledFor, true)} • Click to edit`
                      : "Send or schedule the proposal by email."
                  }
                  completed={Boolean(lead.proposalSent)}
                  onClick={sendProposal}
                  disabled={
                    updating ||
                    isClosed ||
                    !canCompleteStage(ACTION_TYPES.PROPOSAL)
                  }
                />
              )}

              <ActionButton
                icon="⇄"
                title="Negotiation"
                description="Move opportunity to negotiation."
                completed={
                  lead.status === "Negotiation" ||
                  lead.status === "Won" ||
                  Boolean(lead.negotiationStartedAt)
                }
                onClick={startNegotiation}
                disabled={
                  updating ||
                  isClosed ||
                  !canCompleteStage(ACTION_TYPES.NEGOTIATION)
                }
              />

              <ActionButton
                icon="☎"
                title="Call Completed"
                description="Add a completed phone call."
                onClick={markCallCompleted}
                disabled={updating || isClosed}
              />

              <ActionButton
                icon="◷"
                title="Schedule Follow-up"
                description="Set the next follow-up date."
                completed={Boolean(lead.nextFollowUp)}
                onClick={() => {
                  setFollowUpForm({
                    nextFollowUp: lead.nextFollowUp
                      ? String(lead.nextFollowUp).slice(0, 10)
                      : "",
                    followUpNote: "",
                  });

                  setShowFollowUpModal(true);
                }}
                disabled={updating || isClosed}
              />
            </div>

            {(scheduledEmailsLoading || scheduledEmails.length > 0) && (
              <div
                style={{
                  marginTop: 18,
                  borderTop: "1px solid #e2e8f0",
                  paddingTop: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 900,
                    color: "#0f172a",
                    marginBottom: 10,
                  }}
                >
                  Pending Scheduled Emails
                </div>

                {scheduledEmailsLoading ? (
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    Loading schedules...
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    {scheduledEmails.map((item) => (
                      <div
                        key={item._id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 14,
                          padding: "12px 14px",
                          border: "1px solid #bfdbfe",
                          borderRadius: 10,
                          background: "#eff6ff",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 12.5,
                              fontWeight: 900,
                              color: "#1d4ed8",
                            }}
                          >
                            ◷{" "}
                            {MAIL_ACTION_CONFIG[item.actionType]?.buttonLabel ||
                              "Email"}{" "}
                            Scheduled
                          </div>
                          <div
                            style={{
                              marginTop: 3,
                              fontSize: 12,
                              color: "#475569",
                            }}
                          >
                            {formatDate(item.scheduledFor, true)} •{" "}
                            {item.subject}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                          <button
                            type="button"
                            className="lead-detail-secondary-button"
                            onClick={() => openMailBox(item.actionType)}
                            disabled={item.status !== "scheduled"}
                          >
                            Edit Schedule
                          </button>
                          <button
                            type="button"
                            className="lead-detail-lost-button"
                            onClick={() => deleteScheduledEmail(item)}
                            disabled={
                              item.status !== "scheduled" ||
                              deletingScheduledEmailId === String(item._id)
                            }
                          >
                            {deletingScheduledEmailId === String(item._id)
                              ? "Deleting..."
                              : "Delete Schedule"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Tabs */}

          <section className="lead-detail-card">
            <div className="lead-detail-tabs">
              <button
                type="button"
                className={[
                  "lead-detail-tab",
                  activeTab === "activity" ? "lead-detail-active-tab" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setActiveTab("activity")}
              >
                Activity
              </button>

              <button
                type="button"
                className={[
                  "lead-detail-tab",
                  activeTab === "email" ? "lead-detail-active-tab" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setActiveTab("email")}
              >
                Email Thread
                {emailMessages.some(
                  (message) => message.direction === "incoming",
                ) && (
                  <span
                    style={{
                      marginLeft: 6,
                      minWidth: 18,
                      height: 18,
                      padding: "0 5px",
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#dcfce7",
                      color: "#166534",
                      fontSize: 10,
                      fontWeight: 800,
                    }}
                  >
                    {
                      emailMessages.filter(
                        (message) => message.direction === "incoming",
                      ).length
                    }
                  </span>
                )}
              </button>

              <button
                type="button"
                className={[
                  "lead-detail-tab",
                  activeTab === "details" ? "lead-detail-active-tab" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setActiveTab("details")}
              >
                Details
              </button>

              <button
                type="button"
                className={[
                  "lead-detail-tab",
                  activeTab === "team" ? "lead-detail-active-tab" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setActiveTab("team")}
              >
                Contacts
              </button>
            </div>

            {activeTab === "activity" && (
              <div>
                {!isClosed && (
                  <form className="lead-detail-note-form" onSubmit={addNote}>
                    <textarea
                      className="lead-detail-note-input"
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder="Write a note about this lead..."
                      rows={3}
                    />

                    <div className="lead-detail-note-action-row">
                      <button
                        type="submit"
                        className="lead-detail-primary-button"
                        disabled={updating || !note.trim()}
                      >
                        Add Note
                      </button>
                    </div>
                  </form>
                )}

                {activityHistory.length === 0 ? (
                  <div className="lead-detail-empty-activity">
                    <div className="lead-detail-empty-icon">◎</div>
                    <strong>No activity recorded</strong>
                    <span>
                      Use the quick actions above to start tracking this lead.
                    </span>
                  </div>
                ) : (
                  <div className="lead-detail-timeline">
                    {activityHistory.map((activity, index) => (
                      <div
                        key={activity._id || `${activity.createdAt}-${index}`}
                        className="lead-detail-timeline-item"
                      >
                        <div className="lead-detail-timeline-marker">
                          {getActivityIcon(activity.type)}
                        </div>

                        <div className="lead-detail-timeline-content">
                          <div className="lead-detail-timeline-header">
                            <strong className="lead-detail-timeline-title">
                              {activity.title ||
                                activity.type ||
                                "Lead updated"}
                            </strong>

                            <span className="lead-detail-timeline-date">
                              {formatDate(
                                activity.createdAt || activity.date,
                                true,
                              )}
                            </span>
                          </div>

                          {activity.description && (
                            <div className="lead-detail-timeline-description">
                              {activity.description}
                            </div>
                          )}

                          {activity.createdBy && (
                            <div className="lead-detail-timeline-user">
                              By{" "}
                              {activity.createdBy.name ||
                                activity.createdBy.fullName ||
                                activity.createdBy}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "email" && (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: 15,
                        color: "#0f172a",
                      }}
                    >
                      Email Conversation
                    </h3>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 12,
                        color: "#64748b",
                      }}
                    >
                      Client replies are synced from business@sdcstudio.co and
                      shown under the same Outlook conversation.
                    </div>
                  </div>

                  <button
                    type="button"
                    className="lead-detail-primary-button"
                    onClick={syncClientReplies}
                    disabled={syncingReplies}
                  >
                    {syncingReplies ? "Syncing..." : "↻ Sync Replies"}
                  </button>
                </div>

                {emailThreadError && (
                  <div
                    style={{
                      marginBottom: 14,
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: "#fee2e2",
                      color: "#b91c1c",
                      fontSize: 12.5,
                    }}
                  >
                    {emailThreadError}
                  </div>
                )}

                {emailThreadLoading && !emailMessages.length ? (
                  <div
                    style={{
                      padding: 30,
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    Loading email thread...
                  </div>
                ) : emailThreads.length === 0 ? (
                  <div
                    style={{
                      padding: 32,
                      textAlign: "center",
                      border: "1px dashed #cbd5e1",
                      borderRadius: 10,
                      background: "#f8fafc",
                      color: "#64748b",
                    }}
                  >
                    No CRM email conversation found for this lead yet.
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 18,
                    }}
                  >
                    {emailThreads.map((thread) => (
                      <div
                        key={thread.key}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 12,
                          overflow: "hidden",
                          background: "#fff",
                        }}
                      >
                        <div
                          style={{
                            padding: "12px 14px",
                            background: "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                          }}
                        >
                          <strong
                            style={{
                              color: "#0f172a",
                              fontSize: 13.5,
                            }}
                          >
                            {thread.subject}
                          </strong>

                          <div
                            style={{
                              marginTop: 3,
                              color: "#94a3b8",
                              fontSize: 11,
                            }}
                          >
                            {thread.messages.length} message
                            {thread.messages.length === 1 ? "" : "s"} in this
                            thread
                          </div>
                        </div>

                        <div
                          style={{
                            padding: 14,
                            display: "flex",
                            flexDirection: "column",
                            gap: 12,
                          }}
                        >
                          {thread.messages.map((message, index) => {
                            const incoming = message.direction === "incoming";

                            const fromEmail =
                              message?.from?.email ||
                              (incoming
                                ? lead?.email || "Client"
                                : "business@sdcstudio.co");

                            const toEmails = Array.isArray(message?.to)
                              ? message.to
                                  .map((recipient) => recipient?.email)
                                  .filter(Boolean)
                                  .join(", ")
                              : "";

                            const bodyText =
                              stripEmailHtml(message?.body) ||
                              message?.bodyPreview ||
                              "No message body.";

                            return (
                              <div
                                key={
                                  message._id ||
                                  message.graphMessageId ||
                                  `${thread.key}-${index}`
                                }
                                style={{
                                  marginLeft: incoming ? 0 : 28,
                                  marginRight: incoming ? 28 : 0,
                                  border: incoming
                                    ? "1px solid #bbf7d0"
                                    : "1px solid #bfdbfe",
                                  borderRadius: 10,
                                  padding: 14,
                                  background: incoming ? "#f0fdf4" : "#eff6ff",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    alignItems: "flex-start",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <div>
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        padding: "3px 7px",
                                        borderRadius: 999,
                                        fontSize: 10,
                                        fontWeight: 800,
                                        background: incoming
                                          ? "#dcfce7"
                                          : "#dbeafe",
                                        color: incoming ? "#166534" : "#1d4ed8",
                                      }}
                                    >
                                      {incoming
                                        ? "CLIENT REPLY"
                                        : "SENT FROM CRM"}
                                    </span>

                                    <div
                                      style={{
                                        marginTop: 7,
                                        fontSize: 12,
                                        color: "#334155",
                                      }}
                                    >
                                      <strong>From:</strong> {fromEmail}
                                    </div>

                                    {toEmails && (
                                      <div
                                        style={{
                                          marginTop: 2,
                                          fontSize: 11.5,
                                          color: "#64748b",
                                        }}
                                      >
                                        <strong>To:</strong> {toEmails}
                                      </div>
                                    )}
                                  </div>

                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: "#64748b",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {formatDate(
                                      getEmailMessageTime(message),
                                      true,
                                    )}
                                  </div>
                                </div>

                                <div
                                  style={{
                                    marginTop: 12,
                                    paddingTop: 12,
                                    borderTop: incoming
                                      ? "1px solid #dcfce7"
                                      : "1px solid #dbeafe",
                                    whiteSpace: "pre-wrap",
                                    lineHeight: 1.6,
                                    fontSize: 13,
                                    color: "#334155",
                                  }}
                                >
                                  {bodyText}
                                </div>

                                {message.hasAttachments && (
                                  <div
                                    style={{
                                      marginTop: 10,
                                      color: "#64748b",
                                      fontSize: 11,
                                      fontWeight: 700,
                                    }}
                                  >
                                    📎 This email contains attachment(s)
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "details" && (
              <div className="lead-detail-detail-grid">
                <DetailItem label="Company" value={lead.company} />
                <DetailItem label="Lead Source" value={lead.source} />
                <DetailItem label="Priority" value={lead.priority} />
                <DetailItem label="Status" value={lead.status} />

                <DetailItem
                  label="Estimated Value"
                  value={formatCurrency(lead.estValue)}
                />

                {isAdmin && (
                  <DetailItem
                    label="Proposal Value"
                    value={formatCurrency(lead.proposalValue)}
                  />
                )}

                <DetailItem
                  label="Lead Created"
                  value={formatDate(lead.leadCreatedDate || lead.createdAt)}
                />

                <DetailItem
                  label="Expected Closing"
                  value={formatDate(lead.expectedClosing)}
                />

                <DetailItem
                  label="Last Contacted"
                  value={formatDate(lead.lastContactDate)}
                />

                <DetailItem
                  label="Next Follow-up"
                  value={formatDate(lead.nextFollowUp)}
                />

                <DetailItem
                  label="Probability"
                  value={
                    lead.probability !== undefined &&
                    lead.probability !== null &&
                    lead.probability !== ""
                      ? `${lead.probability}%`
                      : "—"
                  }
                />

                <DetailItem
                  label="Assigned To"
                  value={lead.assignedTo || "Not assigned"}
                />

                <DetailItem
                  label="Address"
                  value={[lead.address, lead.city, lead.state]
                    .filter(Boolean)
                    .join(", ")}
                  wide
                />

                {isLost && (
                  <DetailItem
                    label="Lost Reason"
                    value={lead.lostReason}
                    wide
                    danger
                  />
                )}
              </div>
            )}

            {activeTab === "team" && (
              <div className="lead-detail-contact-list">
                <ContactCard
                  contact={{
                    salutation: lead.salutation,
                    firstName: lead.firstName,
                    lastName: lead.lastName,
                    email: lead.email,
                    contactNo: lead.contactNo,
                    designation: lead.designation,
                  }}
                  primary
                />

                {(lead.teamMembers || []).map((member, index) => (
                  <ContactCard key={member._id || index} contact={member} />
                ))}

                {!lead.teamMembers?.length && (
                  <div className="lead-detail-no-additional-contacts">
                    No additional team members have been added.
                  </div>
                )}
              </div>
            )}
          </section>
        </main>

        {/* ===================================================
            RIGHT SIDEBAR
            =================================================== */}

        <aside className="lead-detail-sidebar">
          <section className="lead-detail-card">
            <h2 className="lead-detail-section-title">Primary Contact</h2>

            <div className="lead-detail-contact-profile">
              <div className="lead-detail-contact-avatar">
                {getInitials(getContactName(lead))}
              </div>

              <div>
                <strong className="lead-detail-contact-name">
                  {getContactName(lead)}
                </strong>

                <div className="lead-detail-contact-designation">
                  {lead.designation || "No designation"}
                </div>
              </div>
            </div>

            <ContactRow label="Email" value={lead.email} mail />
            <ContactRow label="Phone" value={lead.contactNo} phone />
            <ContactRow
              label="Location"
              value={[lead.city, lead.state].filter(Boolean).join(", ")}
            />
          </section>

          <section className="lead-detail-card">
            <h2 className="lead-detail-section-title">Opportunity Details</h2>

            <SidebarRow
              label="Estimated Value"
              value={formatCurrency(lead.estValue)}
            />

            <SidebarRow
              label="Expected Closing"
              value={formatDate(lead.expectedClosing)}
            />

            <SidebarRow label="Source" value={lead.source || "—"} />

            <SidebarRow
              label="Probability"
              value={
                lead.probability !== undefined &&
                lead.probability !== null &&
                lead.probability !== ""
                  ? `${lead.probability}%`
                  : "—"
              }
            />

            {isAdmin && (
              <SidebarRow
                label="Proposal Reference"
                value={lead.proposalReference || "—"}
              />
            )}
          </section>

          {lead.meetingDone && (
            <section className="lead-detail-card">
              <h2 className="lead-detail-section-title">Last Meeting</h2>

              <SidebarRow
                label="Meeting Date"
                value={formatDate(lead.meetingDate, true)}
              />

              <SidebarRow
                label="Meeting Mode"
                value={lead.meetingMode || "—"}
              />

              <SidebarRow
                label="Meeting With"
                value={lead.meetingWith || "—"}
              />

              {lead.meetingNotes && (
                <div className="lead-detail-sidebar-notes">
                  {lead.meetingNotes}
                </div>
              )}
            </section>
          )}
        </aside>
      </div>

      {/* =====================================================
          IN-PAGE EMAIL COMPOSER
          ===================================================== */}

      {showMailModal && (
        <Modal
          onClose={() => {
            if (!sendingMail) {
              setShowMailModal(false);
              setEditingScheduledEmail(null);
            }
          }}
          large
        >
          <form onSubmit={sendMailFromLeadPage}>
            <div className="lead-detail-mail-composer-header">
              <div className="lead-detail-modal-icon-mail">✉</div>

              <div>
                <h3 className="lead-detail-modal-title">
                  {editingScheduledEmail
                    ? `Edit ${MAIL_ACTION_CONFIG[mailForm.actionType]?.buttonLabel || "Scheduled Email"}`
                    : MAIL_ACTION_CONFIG[mailForm.actionType]?.title ||
                      "Send Email"}
                </h3>

                <p className="lead-detail-modal-description">
                  {editingScheduledEmail
                    ? "Update the pending schedule. It will remain Scheduled until the actual send time."
                    : "Email will be sent directly from this CRM page. The primary contact and every added member are included automatically."}
                </p>
              </div>
            </div>

            <div className="lead-detail-mail-from-row">
              <span>From</span>
              <strong>business@sdcstudio.co</strong>
            </div>

            <label className="lead-detail-field-label">Recipients *</label>

            <div className="lead-detail-recipient-editor">
              <div className="lead-detail-recipient-list">
                {mailForm.to.map((email) => (
                  <span
                    key={email}
                    className="lead-detail-recipient-chip lead-detail-recipient-chip-editable"
                  >
                    {email}

                    <button
                      type="button"
                      onClick={() => removeMailRecipient(email)}
                      aria-label={`Remove ${email}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              <div className="lead-detail-recipient-add-row">
                <input
                  type="email"
                  className="lead-detail-input"
                  value={mailRecipientInput}
                  onChange={(event) =>
                    setMailRecipientInput(event.target.value)
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addMailRecipients();
                    }
                  }}
                  placeholder="Add another email address"
                />

                <button
                  type="button"
                  className="lead-detail-secondary-button"
                  onClick={addMailRecipients}
                >
                  Add
                </button>
              </div>
            </div>
            <div className="lead-mail-delivery">
              <div className="lead-mail-delivery-heading">
                <div>
                  <strong>Delivery</strong>
                  <span>Choose when this email should be sent</span>
                </div>
              </div>

              <div className="lead-mail-delivery-options">
                <button
                  type="button"
                  className={`lead-mail-delivery-option ${
                    mailSendMode === "now" ? "active" : ""
                  }`}
                  onClick={() => {
                    if (editingScheduledEmail) return;
                    setMailSendMode("now");
                    setScheduledDateTime("");
                  }}
                  disabled={Boolean(editingScheduledEmail)}
                >
                  <div className="lead-mail-delivery-icon">➤</div>

                  <div className="lead-mail-delivery-copy">
                    <strong>Send Now</strong>
                    <span>Send email immediately</span>
                  </div>

                  <div className="lead-mail-radio">
                    {mailSendMode === "now" && <span />}
                  </div>
                </button>

                <button
                  type="button"
                  className={`lead-mail-delivery-option ${
                    mailSendMode === "schedule" ? "active" : ""
                  }`}
                  onClick={() => setMailSendMode("schedule")}
                >
                  <div className="lead-mail-delivery-icon">◷</div>

                  <div className="lead-mail-delivery-copy">
                    <strong>Schedule Send</strong>
                    <span>Choose a date & time</span>
                  </div>

                  <div className="lead-mail-radio">
                    {mailSendMode === "schedule" && <span />}
                  </div>
                </button>
              </div>

              {mailSendMode === "schedule" && (
                <div className="lead-mail-schedule-panel">
                  <div className="lead-mail-schedule-title">
                    <div className="lead-mail-schedule-title-icon">◷</div>

                    <div>
                      <strong>Schedule delivery</strong>
                      <span>
                        Pick a day and time using the new smart scheduler
                      </span>
                    </div>
                  </div>

                  <div className="lead-scheduler-quick-row">
                    <button
                      type="button"
                      onClick={() =>
                        chooseQuickSchedule(getNextScheduleDate(1, 9, 0))
                      }
                    >
                      <span>Tomorrow</span>
                      <strong>9:00 AM</strong>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        chooseQuickSchedule(getNextScheduleDate(1, 14, 0))
                      }
                    >
                      <span>Tomorrow</span>
                      <strong>2:00 PM</strong>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        chooseQuickSchedule(
                          getNextWeekdayScheduleDate(1, 10, 0),
                        )
                      }
                    >
                      <span>Next Monday</span>
                      <strong>10:00 AM</strong>
                    </button>
                  </div>

                  <div className="lead-modern-scheduler">
                    <div className="lead-modern-calendar">
                      <div className="lead-modern-calendar-header">
                        <button
                          type="button"
                          aria-label="Previous month"
                          onClick={() =>
                            setScheduleCalendarMonth((current) => {
                              const next = new Date(current);
                              next.setMonth(next.getMonth() - 1);
                              return next;
                            })
                          }
                        >
                          ‹
                        </button>

                        <strong>
                          {scheduleCalendarMonth.toLocaleDateString("en-US", {
                            month: "long",
                            year: "numeric",
                          })}
                        </strong>

                        <button
                          type="button"
                          aria-label="Next month"
                          onClick={() =>
                            setScheduleCalendarMonth((current) => {
                              const next = new Date(current);
                              next.setMonth(next.getMonth() + 1);
                              return next;
                            })
                          }
                        >
                          ›
                        </button>
                      </div>

                      <div className="lead-modern-calendar-weekdays">
                        {["M", "T", "W", "T", "F", "S", "S"].map(
                          (day, index) => (
                            <span key={`${day}-${index}`}>{day}</span>
                          ),
                        )}
                      </div>

                      <div className="lead-modern-calendar-grid">
                        {scheduleCalendarDays.map((date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);

                          const dateOnly = new Date(date);
                          dateOnly.setHours(0, 0, 0, 0);

                          const isPast = dateOnly < today;

                          const isOutsideMonth =
                            date.getMonth() !==
                            scheduleCalendarMonth.getMonth();

                          const isToday =
                            dateOnly.getTime() === today.getTime();

                          const isSelected =
                            selectedScheduleDate &&
                            date.getFullYear() ===
                              selectedScheduleDate.getFullYear() &&
                            date.getMonth() ===
                              selectedScheduleDate.getMonth() &&
                            date.getDate() === selectedScheduleDate.getDate();

                          return (
                            <button
                              type="button"
                              key={date.toISOString()}
                              disabled={isPast}
                              className={[
                                isOutsideMonth ? "outside-month" : "",
                                isToday ? "today" : "",
                                isSelected ? "selected" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              onClick={() => chooseScheduleDate(date)}
                            >
                              {date.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="lead-modern-time-panel">
                      <div className="lead-modern-time-heading">
                        <span>TIME</span>
                        <strong>
                          {selectedScheduleDate
                            ? selectedScheduleDate.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                              })
                            : "Select date"}
                        </strong>
                      </div>

                      <div className="lead-modern-time-slots">
                        {[
                          "08:00",
                          "09:00",
                          "10:00",
                          "11:00",
                          "12:00",
                          "13:00",
                          "14:00",
                          "15:00",
                          "16:00",
                          "17:00",
                          "18:00",
                          "19:00",
                        ].map((timeValue) => (
                          <button
                            type="button"
                            key={timeValue}
                            className={
                              selectedScheduleTime === timeValue
                                ? "selected"
                                : ""
                            }
                            onClick={() => chooseScheduleTime(timeValue)}
                          >
                            {new Date(
                              `2000-01-01T${timeValue}:00`,
                            ).toLocaleTimeString("en-US", {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </button>
                        ))}
                      </div>

                      <div className="lead-modern-custom-time">
                        <label>Custom time</label>
                        <input
                          type="time"
                          value={selectedScheduleTime}
                          onChange={(event) =>
                            chooseScheduleTime(event.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="lead-mail-timezone">
                    <span className="lead-mail-timezone-icon">◷</span>

                    <div>
                      <strong>
                        {Intl.DateTimeFormat().resolvedOptions().timeZone ||
                          "Asia/Kolkata"}
                      </strong>
                      <span>
                        Time is saved in your local timezone and converted
                        safely before sending.
                      </span>
                    </div>
                  </div>

                  {scheduledDateTime && (
                    <div className="lead-mail-schedule-preview">
                      <span>✓</span>

                      <div>
                        <small>EMAIL WILL SEND ON</small>
                        <strong>
                          {new Date(scheduledDateTime).toLocaleString("en-IN", {
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </strong>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <label className="lead-detail-field-label">Subject *</label>

            <input
              className="lead-detail-input"
              value={mailForm.subject}
              onChange={(event) =>
                setMailForm((previous) => ({
                  ...previous,
                  subject: event.target.value,
                }))
              }
              placeholder="Email subject"
              required
            />

            {mailForm.actionType === "proposal" && (
              <div className="lead-detail-proposal-fields">
                <label className="lead-detail-field-label">Proposal Date</label>

                <input
                  type="date"
                  className="lead-detail-input"
                  value={proposalForm.proposalDate}
                  onChange={(event) =>
                    setProposalForm((previous) => ({
                      ...previous,
                      proposalDate: event.target.value,
                    }))
                  }
                />

                <label className="lead-detail-field-label">
                  Proposal Notes
                </label>

                <textarea
                  className="lead-detail-textarea"
                  value={proposalForm.proposalNotes}
                  onChange={(event) =>
                    setProposalForm((previous) => ({
                      ...previous,
                      proposalNotes: event.target.value,
                    }))
                  }
                  placeholder="Scope, exclusions, payment terms or other details"
                  rows={3}
                />

                <button
                  type="button"
                  className="lead-detail-secondary-button"
                  onClick={() => {
                    const template = buildMailTemplate("proposal");
                    setMailForm((previous) => ({
                      ...previous,
                      subject: template.subject,
                      message: template.message,
                    }));
                  }}
                >
                  ↻ Update message with these details
                </button>
              </div>
            )}

            <label className="lead-detail-field-label">Message *</label>

            <textarea
              className="lead-detail-textarea lead-detail-mail-message"
              value={mailForm.message}
              onChange={(event) =>
                setMailForm((previous) => ({
                  ...previous,
                  message: event.target.value,
                }))
              }
              rows={12}
              placeholder="Write email message"
              required
            />

            <label className="lead-detail-field-label">
              Attachments (maximum 5 files, 10 MB each)
            </label>

            <label className="lead-detail-file-picker">
              <input
                type="file"
                multiple
                onChange={handleMailFiles}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.zip"
              />
              <span>＋ Add Attachments</span>
            </label>

            {mailForm.attachments.length > 0 && (
              <div className="lead-detail-attachment-list">
                {mailForm.attachments.map((file, index) => (
                  <div
                    key={`${file.name}-${file.lastModified}-${index}`}
                    className="lead-detail-attachment-item"
                  >
                    <div>
                      <strong>{file.name}</strong>
                      <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeMailAttachment(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="lead-detail-mail-pipeline-note">
              {mailSendMode === "schedule" ? (
                <>
                  Email will be sent automatically at the selected time. The
                  sales pipeline will update only after the email is actually
                  sent.
                </>
              ) : (
                <>
                  After successful delivery, the sales pipeline will
                  automatically move to{" "}
                  <strong>
                    {MAIL_ACTION_CONFIG[mailForm.actionType]?.status}
                  </strong>
                  .
                </>
              )}
            </div>

            <div className="lead-detail-modal-actions">
              <button
                type="button"
                className="lead-detail-secondary-button"
                onClick={() => {
                  setShowMailModal(false);
                  setEditingScheduledEmail(null);
                }}
                disabled={sendingMail}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="lead-detail-primary-button"
                disabled={sendingMail || !mailForm.to.length}
              >
                {sendingMail
                  ? mailSendMode === "schedule"
                    ? "Scheduling Email..."
                    : "Sending Email..."
                  : mailSendMode === "schedule"
                    ? editingScheduledEmail
                      ? "Update Schedule"
                      : "Schedule Email"
                    : `Send to ${mailForm.to.length} Recipient${
                        mailForm.to.length === 1 ? "" : "s"
                      }`}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* =====================================================
          LOST MODAL
          ===================================================== */}

      {showLostModal && (
        <Modal onClose={() => setShowLostModal(false)}>
          <form onSubmit={markAsLost}>
            <div className="lead-detail-modal-icon-lost">×</div>

            <h3 className="lead-detail-modal-title">Mark Lead as Lost</h3>

            <p className="lead-detail-modal-description">
              Select the reason why this opportunity was lost.
            </p>

            <label className="lead-detail-field-label">Lost Reason *</label>

            <select
              className="lead-detail-input"
              value={lostReason}
              onChange={(event) => setLostReason(event.target.value)}
              required
            >
              <option value="">Select reason</option>

              {LOST_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>

            {lostReason === "Other" && (
              <>
                <label className="lead-detail-field-label">
                  Enter Reason *
                </label>

                <textarea
                  className="lead-detail-textarea"
                  value={customLostReason}
                  onChange={(event) => setCustomLostReason(event.target.value)}
                  placeholder="Explain why the lead was lost"
                  rows={3}
                  required
                />
              </>
            )}

            <div className="lead-detail-modal-actions">
              <button
                type="button"
                className="lead-detail-secondary-button"
                onClick={() => setShowLostModal(false)}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="lead-detail-lost-button"
                disabled={updating}
              >
                {updating ? "Saving..." : "Mark as Lost"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* =====================================================
          MEETING MODAL
          ===================================================== */}

      {showMeetingModal && (
        <Modal onClose={() => setShowMeetingModal(false)}>
          <form onSubmit={saveMeeting}>
            <h3 className="lead-detail-modal-title">Record Client Meeting</h3>

            <p className="lead-detail-modal-description">
              Add meeting details and move the lead to Meeting Done.
            </p>

            <label className="lead-detail-field-label">
              Meeting Date & Time *
            </label>

            <input
              type="datetime-local"
              className="lead-detail-input"
              value={meetingForm.meetingDate}
              onChange={(event) =>
                setMeetingForm((previous) => ({
                  ...previous,
                  meetingDate: event.target.value,
                }))
              }
              required
            />

            <label className="lead-detail-field-label">Meeting Mode</label>

            <select
              className="lead-detail-input"
              value={meetingForm.meetingMode}
              onChange={(event) =>
                setMeetingForm((previous) => ({
                  ...previous,
                  meetingMode: event.target.value,
                }))
              }
            >
              <option value="Online">Online</option>
              <option value="Client Office">Client Office</option>
              <option value="Our Office">Our Office</option>
              <option value="Site Visit">Site Visit</option>
              <option value="Phone Call">Phone Call</option>
            </select>

            <label className="lead-detail-field-label">Meeting With</label>

            <input
              className="lead-detail-input"
              value={meetingForm.meetingWith}
              onChange={(event) =>
                setMeetingForm((previous) => ({
                  ...previous,
                  meetingWith: event.target.value,
                }))
              }
              placeholder="Client or participant name"
            />

            <label className="lead-detail-field-label">Meeting Notes</label>

            <textarea
              className="lead-detail-textarea"
              value={meetingForm.meetingNotes}
              onChange={(event) =>
                setMeetingForm((previous) => ({
                  ...previous,
                  meetingNotes: event.target.value,
                }))
              }
              placeholder="Discussion summary, requirements and next action"
              rows={4}
            />

            <div className="lead-detail-modal-actions">
              <button
                type="button"
                className="lead-detail-secondary-button"
                onClick={() => setShowMeetingModal(false)}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="lead-detail-primary-button"
                disabled={updating}
              >
                {updating ? "Saving..." : "Save Meeting"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* =====================================================
          FOLLOW-UP MODAL
          ===================================================== */}

      {showFollowUpModal && (
        <Modal onClose={() => setShowFollowUpModal(false)}>
          <form onSubmit={saveFollowUp}>
            <h3 className="lead-detail-modal-title">Schedule Follow-up</h3>

            <p className="lead-detail-modal-description">
              Select the next client follow-up date.
            </p>

            <label className="lead-detail-field-label">Follow-up Date *</label>

            <input
              type="date"
              className="lead-detail-input"
              value={followUpForm.nextFollowUp}
              onChange={(event) =>
                setFollowUpForm((previous) => ({
                  ...previous,
                  nextFollowUp: event.target.value,
                }))
              }
              required
            />

            <label className="lead-detail-field-label">Follow-up Note</label>

            <textarea
              className="lead-detail-textarea"
              value={followUpForm.followUpNote}
              onChange={(event) =>
                setFollowUpForm((previous) => ({
                  ...previous,
                  followUpNote: event.target.value,
                }))
              }
              placeholder="What needs to be discussed during the follow-up?"
              rows={3}
            />

            <div className="lead-detail-modal-actions">
              <button
                type="button"
                className="lead-detail-secondary-button"
                onClick={() => setShowFollowUpModal(false)}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="lead-detail-primary-button"
                disabled={updating}
              >
                {updating ? "Saving..." : "Schedule Follow-up"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

/* =========================================================
   SMALL COMPONENTS
   ========================================================= */

const SummaryCard = ({ title, value, valueClassName = "" }) => (
  <div className="lead-detail-summary-card">
    <div className="lead-detail-summary-label">{title}</div>

    <div
      className={["lead-detail-summary-value", valueClassName]
        .filter(Boolean)
        .join(" ")}
    >
      {value || "—"}
    </div>
  </div>
);

const ActionButton = ({
  icon,
  title,
  description,
  completed,
  onClick,
  disabled,
}) => (
  <button
    type="button"
    className={[
      "lead-detail-action-button",
      completed ? "lead-detail-action-button-completed" : "",
      disabled ? "lead-detail-disabled-button" : "",
    ]
      .filter(Boolean)
      .join(" ")}
    onClick={onClick}
    disabled={disabled}
  >
    <span
      className={[
        "lead-detail-action-icon",
        completed ? "lead-detail-action-icon-completed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {completed ? "✓" : icon}
    </span>

    <span>
      <strong className="lead-detail-action-title">{title}</strong>

      <span className="lead-detail-action-description">{description}</span>
    </span>
  </button>
);

const DetailItem = ({ label, value, wide, danger }) => (
  <div
    className={[
      "lead-detail-detail-item",
      wide ? "lead-detail-detail-item-wide" : "",
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <div className="lead-detail-detail-label">{label}</div>

    <div
      className={[
        "lead-detail-detail-value",
        danger ? "lead-detail-detail-value-danger" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {value || "—"}
    </div>
  </div>
);

const ContactCard = ({ contact, primary }) => {
  const name =
    `${contact?.salutation || ""} ${contact?.firstName || ""} ${
      contact?.lastName || ""
    }`.trim() || "Unnamed contact";

  return (
    <div className="lead-detail-contact-card">
      <div className="lead-detail-small-contact-avatar">
        {getInitials(name)}
      </div>

      <div className="lead-detail-contact-card-content">
        <div className="lead-detail-contact-card-name-row">
          <strong>{name}</strong>

          {primary && (
            <span className="lead-detail-primary-badge">Primary</span>
          )}
        </div>

        <div className="lead-detail-contact-card-designation">
          {contact?.designation || "No designation"}
        </div>

        <div className="lead-detail-contact-card-meta">
          {contact?.email && (
            <a
              href={`mailto:${contact.email}`}
              className="lead-detail-contact-link"
            >
              {contact.email}
            </a>
          )}

          {contact?.contactNo && (
            <a
              href={`tel:${contact.contactNo}`}
              className="lead-detail-contact-link"
            >
              {contact.contactNo}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const ContactRow = ({ label, value, mail, phone }) => {
  let content = value || "—";

  if (value && mail) {
    content = (
      <a href={`mailto:${value}`} className="lead-detail-contact-link">
        {value}
      </a>
    );
  }

  if (value && phone) {
    content = (
      <a href={`tel:${value}`} className="lead-detail-contact-link">
        {value}
      </a>
    );
  }

  return (
    <div className="lead-detail-contact-row">
      <span className="lead-detail-contact-row-label">{label}</span>
      <span className="lead-detail-contact-row-value">{content}</span>
    </div>
  );
};

const SidebarRow = ({ label, value }) => (
  <div className="lead-detail-sidebar-row">
    <span className="lead-detail-sidebar-label">{label}</span>
    <strong className="lead-detail-sidebar-value">{value || "—"}</strong>
  </div>
);

const Modal = ({ children, onClose, large = false }) => (
  <div className="lead-detail-overlay" onMouseDown={onClose}>
    <div
      className={["lead-detail-modal", large ? "lead-detail-modal-large" : ""]
        .filter(Boolean)
        .join(" ")}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="lead-detail-modal-close"
        onClick={onClose}
      >
        ×
      </button>

      {children}
    </div>
  </div>
);

const getActivityIcon = (type = "") => {
  const normalizedType = String(type).toLowerCase();

  if (normalizedType.includes("email")) return "✉";
  if (normalizedType.includes("portfolio")) return "▣";
  if (normalizedType.includes("meeting")) return "◉";
  if (normalizedType.includes("proposal")) return "₹";
  if (normalizedType.includes("negotiation")) return "⇄";
  if (normalizedType.includes("won")) return "✓";
  if (normalizedType.includes("lost")) return "×";
  if (normalizedType.includes("call")) return "☎";
  if (normalizedType.includes("follow")) return "◷";
  if (normalizedType.includes("note")) return "✎";

  return "•";
};

export default LeadDetail;
